import { RangeSetBuilder } from "@codemirror/state";
import { markdown } from "@codemirror/lang-markdown";
import { Decoration, EditorView, ViewPlugin, placeholder, type DecorationSet, type ViewUpdate } from "@codemirror/view";
import CodeMirror from "@uiw/react-codemirror";
import { useMemo } from "react";

import { cn } from "@/lib/utils";

type MarkdownLineKind = "blank" | "heading" | "paragraph" | "list" | "blockquote" | "fence";

type MarkdownLineInfo = {
  from: number;
  kind: MarkdownLineKind;
  headingLevel?: number;
};

type MarkdownLineClassification = {
  kind: MarkdownLineKind;
  headingLevel?: number;
  togglesFence: boolean;
};

type MarkdownEditorChrome = "default" | "embedded";

const FENCE_PATTERN = /^(```+|~~~+)/;
const HEADING_PATTERN = /^(#{1,6})\s+/;
const LIST_PATTERN = /^(\s*)([-+*]|\d+\.)\s+/;
const BLOCKQUOTE_PATTERN = /^\s*>\s?/;

export const classifyMarkdownLine = (lineText: string, inFence: boolean): MarkdownLineClassification => {
  if (inFence) {
    return {
      kind: "fence",
      togglesFence: FENCE_PATTERN.test(lineText.trim())
    };
  }

  const trimmedLine = lineText.trim();
  if (trimmedLine.length === 0) {
    return { kind: "blank", togglesFence: false };
  }

  if (FENCE_PATTERN.test(trimmedLine)) {
    return { kind: "fence", togglesFence: true };
  }

  const headingMatch = lineText.match(HEADING_PATTERN);
  if (headingMatch) {
    return {
      kind: "heading",
      headingLevel: headingMatch[1].length,
      togglesFence: false
    };
  }

  if (LIST_PATTERN.test(lineText)) {
    return { kind: "list", togglesFence: false };
  }

  if (BLOCKQUOTE_PATTERN.test(lineText)) {
    return { kind: "blockquote", togglesFence: false };
  }

  return { kind: "paragraph", togglesFence: false };
};

const linesBelongToSameBlock = (currentLine: MarkdownLineInfo, adjacentLine?: MarkdownLineInfo): boolean => {
  if (!adjacentLine) {
    return false;
  }

  if (currentLine.kind !== adjacentLine.kind) {
    return false;
  }

  return currentLine.kind === "paragraph" || currentLine.kind === "list" || currentLine.kind === "blockquote" || currentLine.kind === "fence";
};

const buildMarkdownTypographyDecorations = (view: EditorView): DecorationSet => {
  const lines: MarkdownLineInfo[] = [];
  const { doc } = view.state;
  let inFence = false;

  for (let lineNumber = 1; lineNumber <= doc.lines; lineNumber += 1) {
    const line = doc.line(lineNumber);
    const classification = classifyMarkdownLine(line.text, inFence);

    lines.push({
      from: line.from,
      kind: classification.kind,
      headingLevel: classification.headingLevel
    });

    if (classification.togglesFence) {
      inFence = !inFence;
    }
  }

  const builder = new RangeSetBuilder<Decoration>();
  for (const [index, line] of lines.entries()) {
    const classes = ["cm-md-line"];

    if (line.kind === "blank") {
      classes.push("cm-md-blank");
    } else if (line.kind === "heading") {
      classes.push("cm-md-heading", `cm-md-heading-${line.headingLevel ?? 1}`);
    } else {
      classes.push(`cm-md-${line.kind}`);
    }

    if (!linesBelongToSameBlock(line, lines[index - 1])) {
      classes.push("cm-md-block-start");
    }

    if (!linesBelongToSameBlock(line, lines[index + 1])) {
      classes.push("cm-md-block-end");
    }

    builder.add(
      line.from,
      line.from,
      Decoration.line({
        attributes: {
          class: classes.join(" ")
        }
      })
    );
  }

  return builder.finish();
};

const markdownTypography = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;

    constructor(view: EditorView) {
      this.decorations = buildMarkdownTypographyDecorations(view);
    }

    update(update: ViewUpdate): void {
      if (update.docChanged || update.viewportChanged) {
        this.decorations = buildMarkdownTypographyDecorations(update.view);
      }
    }
  },
  {
    decorations: (pluginValue) => pluginValue.decorations
  }
);

type MarkdownEditorProps = {
  value: string;
  onChange: (value: string) => void;
  placeholderText?: string;
  minHeight?: string;
  className?: string;
  chrome?: MarkdownEditorChrome;
  fillHeight?: boolean;
};

export const MarkdownEditor = ({
  value,
  onChange,
  placeholderText,
  minHeight = "600px",
  className,
  chrome = "default",
  fillHeight = false
}: MarkdownEditorProps) => {
  const extensions = useMemo(
    () => [
      markdown(),
      EditorView.lineWrapping,
      markdownTypography,
      ...(placeholderText ? [placeholder(placeholderText)] : [])
    ],
    [placeholderText]
  );

  return (
    <CodeMirror
      value={value}
      minHeight={fillHeight ? undefined : minHeight}
      height={fillHeight ? "100%" : undefined}
      basicSetup={{
        lineNumbers: false,
        foldGutter: false,
        highlightActiveLineGutter: false
      }}
      extensions={extensions}
      onChange={onChange}
      className={cn(
        "markdown-editor",
        chrome === "embedded" && "markdown-editor--embedded",
        fillHeight && "markdown-editor--fill",
        className
      )}
    />
  );
};
